import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductCategoryForm from './ProductCategoryForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill, SystemPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const linkButtonStyle = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 14, cursor: 'pointer', textDecoration: 'underline' };

const sameParent = (a, b) => String(a.parent_id || '') === String(b.parent_id || '');

function ProductCategoryTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  // Full, unpaginated tree — needed by ProductCategoryForm's parent-category
  // dropdown, which must offer every category regardless of what page the
  // table below is showing.
  const [allCategories, setAllCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  // Drag-to-reorder: a row only becomes draggable while its ☰ handle is
  // held (dragArmedId), so clicking anywhere else on the row still edits.
  // A drop only lands on a sibling under the same parent — the backend's
  // reorder endpoint enforces the same scope.
  const [dragArmedId, setDragArmedId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchAllCategories();
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [page, limit]);

  const fetchAllCategories = async () => {
    try {
      const res = await axios.get('/api/product-categories');
      setAllCategories(res.data.data || []);
    } catch (err) {
      setAllCategories([]);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/product-categories', { params: { page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const refetch = () => {
    fetchRows();
    fetchAllCategories();
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate category "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/product-categories/${item.id}/deactivate`);
      refetch();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/product-categories/${item.id}/reactivate`);
      refetch();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const canDropOn = (target) => draggedItem && target.id !== draggedItem.id && sameParent(target, draggedItem);

  const endDrag = () => {
    setDragArmedId(null);
    setDraggedItem(null);
    setDropTargetId(null);
  };

  const handleDrop = async (target) => {
    const dragged = draggedItem;
    endDrag();
    if (!dragged || !canDropOn(target)) return;
    // Siblings come from the full tree, not just this page's rows, so the
    // reorder always covers every Category under the parent
    const siblings = allCategories.filter((c) => sameParent(c, dragged));
    const fromIdx = siblings.findIndex((c) => c.id === dragged.id);
    const toIdx = siblings.findIndex((c) => c.id === target.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...siblings];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    try {
      await axios.patch('/api/product-categories/reorder', {
        parent_id: dragged.parent_id || null,
        ordered_ids: reordered.map((c) => c.id),
      });
      refetch();
      dismissError();
    } catch (err) {
      showError(err, 'Reorder failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/product-categories/${editItem.id}`, data);
      } else {
        await axios.post('/api/product-categories', data);
      }
      setShowForm(false);
      refetch();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Product Categories</h2>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add Category
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <ProductCategoryForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            categories={allCategories}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '54%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                draggable={dragArmedId === item.id}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(item.id));
                  setDraggedItem(item);
                }}
                onDragOver={(e) => {
                  if (!canDropOn(item)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dropTargetId !== item.id) setDropTargetId(item.id);
                }}
                onDragLeave={() => { if (dropTargetId === item.id) setDropTargetId(null); }}
                onDrop={(e) => { e.preventDefault(); handleDrop(item); }}
                onDragEnd={endDrag}
                style={{
                  cursor: showForm || !canUpdate ? 'default' : 'pointer',
                  opacity: draggedItem?.id === item.id ? 0.4 : 1,
                  background: dropTargetId === item.id ? '#e3f2fd' : undefined,
                }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td
                  style={{ padding: '6px 4px', textAlign: 'center', fontSize: 16, color: '#888', cursor: showForm ? 'not-allowed' : 'grab', userSelect: 'none' }}
                  title={showForm ? 'Finish editing first' : 'Drag to reorder'}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={() => { if (!showForm) setDragArmedId(item.id); }}
                  onMouseUp={() => setDragArmedId(null)}
                >
                  ☰
                </td>
                <td style={{ padding: '6px 8px', paddingLeft: 8 + item.depth * 24, fontSize: 14 }}>
                  {item.depth > 0 ? '— ' : ''}{item.name}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  {item.is_system_default
                    ? <SystemPill title="System Use — click to rename; required by Optim's core functionality." />
                    : <ActiveStatusPill active={item.is_active} />}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.usage_count ? (
                    <button type="button" style={linkButtonStyle} onClick={() => navigate(`/products?category=${item.id}`)}>
                      {item.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      {
                        key: 'edit',
                        label: 'Edit',
                        disabled: showForm,
                        disabledTitle: showForm ? 'Finish editing first' : undefined,
                        onClick: () => handleEdit(item),
                      },
                      item.is_active
                        ? {
                            key: 'deactivate',
                            label: 'Deactivate',
                            danger: true,
                            disabled: showForm || item.is_system_default,
                            disabledTitle: showForm ? 'Finish editing first' : 'The default Uncategorised category cannot be deactivated',
                            onClick: () => handleDeactivate(item),
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleReactivate(item),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Root Categories"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ProductCategoryTable;
